'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';
import type { IProductIDData } from '@surfside/ads-react';

import {
  EnrichedProduct,
  findSurfsideProductById,
  fireCarouselPixels,
  fireViewableTrackers,
  getFirstSponsoredCard,
} from './utils';

type CarouselAnalytics = {
  trackWin: (_product: IProductIDData) => void;
  trackImpression: (_product: IProductIDData) => void;
  trackClick: (_product: IProductIDData) => void;
};

export const useCarouselTracking = (
  surfsideProducts: IProductIDData[] | undefined,
  enrichedProducts: EnrichedProduct[],
  analytics: CarouselAnalytics | undefined,
) => {
  const seenWinUrls = useRef<Set<string>>(new Set());
  const seenImpressionUrls = useRef<Set<string>>(new Set());
  const seenViewables = useRef<Set<string>>(new Set());
  const surfsideProductsRef = useRef(surfsideProducts);
  const analyticsRef = useRef(analytics);
  surfsideProductsRef.current = surfsideProducts;
  analyticsRef.current = analytics;

  useLayoutEffect(() => {
    if (!analytics || !enrichedProducts.length) return;

    const firstSponsoredCard = getFirstSponsoredCard(
      surfsideProducts,
      enrichedProducts,
    );
    if (!firstSponsoredCard) return;

    fireCarouselPixels(
      firstSponsoredCard,
      analytics,
      seenWinUrls.current,
      seenImpressionUrls.current,
    );
  }, [analytics, enrichedProducts, surfsideProducts]);

  const onProductClick = useCallback((product: EnrichedProduct) => {
    const currentAnalytics = analyticsRef.current;
    if (!currentAnalytics) return;

    const id = product.id ? String(product.id).trim() : undefined;
    const surfsideProduct = findSurfsideProductById(
      surfsideProductsRef.current,
      id,
    );

    if (surfsideProduct) {
      currentAnalytics.trackClick(surfsideProduct);
    }
  }, []);

  const onProductViewable = useCallback((product: EnrichedProduct) => {
    if (product.surfsideType !== 'sponsored') return;

    const id = product.id ? String(product.id).trim() : undefined;
    if (!id || seenViewables.current.has(id)) return;

    const surfsideProduct = findSurfsideProductById(
      surfsideProductsRef.current,
      id,
    );
    if (!surfsideProduct) return;

    fireViewableTrackers(surfsideProduct);
    seenViewables.current.add(id);
  }, []);

  return { onProductClick, onProductViewable };
};
